import React, { Component } from "react";
import Context from "../../Context";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import Strings from "@my-dld/assets/strings";
import TextInputField from "@eres/forms-web/TextInputField";
import NumericStringConverter from "@eres/forms/converters/NumericStringConverter";
import CurrencyInputField from "@eres/forms-web/CurrencyInputField";
import FormRow from "@eres/forms-web/FormRow";
import { Lookup } from "@eres/ejari-api/Entity";
import { HTRequestType } from "../../api/entities";
import { populateLookup } from "../../Utils";
import { HTLookUps } from "../../api/constants";
import { IdExtractor } from "@my-dld/utils/LookupUtil";
import PickerInput from "@eres/forms-web/PickerInput";
import { AlphaSortProcessor } from "@eres/ejari-api/Utils";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "@eres/ejari-web/utils";
import { defaultErrorManager } from "@eres/forms/errormanager";
import { getLocalValue } from "@eres/core/dist/LocaleValue";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { toFormattedCurrency } from "@eres/core/dist/Currency";

interface Props {
	mode: "edit" | "view";
}

export interface DescriptionData {
	status?: Lookup;
	unitKeys?: string;
	parking?: string;
	amount?: string;
	notes?: string;
}

interface State extends DescriptionData {
	statuses?: Lookup[];
	errorContainer: ErrorContainer;
}

class Description extends Component<Props, State> {
	static contextType = Context;
	context!: React.ContextType<typeof Context>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
		};
	}

	componentDidMount() {
		this.setRequestData();
		if ( !this.isHandOver() ) {
			populateLookup( HTLookUps.PROPERTY_CONDITIONS, {
				onLookup: ( lookup: HTLookUps | string, values: Lookup[] ) => {
					this.setState( {
						statuses: values,
						status: this.getSelectedStatus( values ),
					} );
				}
			}, AlphaSortProcessor );
		}
	}

	render() {
		const { statuses } = this.state;
		const isHandOver = this.isHandOver();
		if ( this.props.mode === "view" ) {
			return <RenderInfo state={ this.state } isHandOver={ isHandOver }/>;
		}
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group field-style",
			errorManager: defaultErrorManager,
		};
		const notesProps: Partial<WebFormFieldProps<any, any>> = {
			...filedProps,
			containerClassName: "col-md-12 field-style",
		};
		return <div className="card mb-3">
			<div className="card-header">
				<span>{ Strings.description }</span>
			</div>
			<div className="card-body">
				<div className="mandatory-field-info">
					<small>{ Strings.mandatory_fields_note }</small>
				</div>
				<FormRow { ...filedProps }>
					{ !isHandOver && <PickerInput
						required={ true }
						containerClassName="col-xl-4 col-lg-6 col-md-8 col-sm-12 col-12"
						id={ "status" }
						label={ Strings.current_status_property }
						isLoading={ statuses === undefined }
						items={ statuses }
						toIdentifier={ IdExtractor }/> }
					<TextInputField
						required={ true }
						containerClassName="col-xl-4 col-lg-6 col-md-8 col-sm-12 col-12"
						id={ "unitKeys" }
						label={ Strings.property_key_qty }
						valueConverter={ NumericStringConverter }/>
					<TextInputField
						required={ true }
						containerClassName="col-xl-4 col-lg-6 col-md-8 col-sm-12 col-12"
						id={ "parking" }
						label={ Strings.parking_entrance_cards }
						valueConverter={ NumericStringConverter }/>
					{ isHandOver && <CurrencyInputField
						id={ "amount" }
						containerClassName="col-xl-4 col-lg-6 col-md-8 col-sm-12 col-12"
						label={ Strings.security_deposit_amount }
						disabled={ true }/> }
				</FormRow>
				<FormRow { ...notesProps }>
					<TextInputField
						id={ "notes" }
						label={ Strings.notes }
						customInput={ <textarea rows={ 4 }/> }
						maxLength={ 500 }/>
				</FormRow>
			</div>
		</div>;
	}

	public getDescription(): Promise<DescriptionData> {
		const state = this.state;
		const error: ErrorContainer = {};
		const isHandOver = this.isHandOver();
		let isValid = checkNonEmpty( state, "unitKeys", error );
		const parking = Number( state.parking );
		if ( isNaN( parking ) ) {
			error.parking = Strings.required;
			isValid = false;
		}
		if ( !isHandOver ) {
			if ( state.status == undefined ) {
				error.status = Strings.required;
				isValid = false;
			}
		}
		this.setState( {
			errorContainer: error,
		} );
		if ( isValid ) {
			return Promise.resolve( {
				status: isHandOver ? undefined : state.status,
				unitKeys: state.unitKeys,
				parking: state.parking,
				amount: isHandOver ? state.amount : undefined,
				notes: state.notes,
			} );
		}
		return Promise.reject( "Description not valid" );
	}

	private isHandOver() {
		const { request } = this.context;
		return request?.RequestTypeId === HTRequestType.HANDOVER;
	};

	private setRequestData() {
		const { request } = this.context;
		if ( request && request.Properties && request.Properties.length > 0 ) {
			const property = request.Properties[ 0 ];
			this.setState( {
				unitKeys: property.UnitKeys ? "" + property.UnitKeys : "",
				parking: property.BuildingCards ? "" + property.BuildingCards : "",
				amount: request.PaidSecurityDeposit ? "" + request.PaidSecurityDeposit : "",
				notes: request.Notes,
			} );
		}
	}

	private getSelectedStatus( statuses: Lookup[] ) {
		const { request } = this.context;
		if ( request && request.Properties && request.Properties.length > 0 ) {
			const property = request.Properties[ 0 ];
			const sStatus = statuses.find( status => status.Id === property.PropertyConditionId );
			if ( sStatus ) {
				return sStatus;
			}
		}
		return undefined;
	}
}

function RenderInfo( props: { state: State, isHandOver: boolean } ) {
	const { status, unitKeys, parking, amount, notes } = props.state;
	let fAmount = "-";
	if ( amount && !isEmptyStringOrNull( amount ) ) {
		fAmount = toFormattedCurrency( Number( amount ) ) + " " + Strings.aed;
	}
	return <>
		<div className="section-title">
			<h5>{ Strings.description }</h5>
		</div>
		<div className="flex-grid">
			{ !props.isHandOver && <div>
				<span>{ Strings.current_status_unit }</span>
				<span>{ getLocalValue( status ) }</span>
			</div> }
			<div>
				<span>{ Strings.property_key_qty }</span>
				<span>{ unitKeys }</span>
			</div>
			<div>
				<span>{ Strings.parking_entrance_cards }</span>
				<span>{ parking }</span>
			</div>
			{ props.isHandOver && <div>
				<span>{ Strings.security_deposit_amount }</span>
				<span>{ fAmount }</span>
			</div> }
			{ !isEmptyStringOrNull( notes ) && <div>
				<span>{ Strings.notes }</span>
				<span>{ notes }</span>
			</div> }
		</div>
	</>;
}

export default Description;
